// @flow
import * as React from 'react';

import { Plus } from 'lucide-react';

import { Loader, View } from 'reshaped';

import { selectCartReselleeID } from '@gandi/cart';
import { Link } from '@gandi/reshaped';
import { localeSelector, useTranslator } from '@gandi/translate';

import {
  selectCustomersList,
  selectCustomersCount,
  LIST_API_LIMIT,
} from 'src/Cart/Customer/modules/customers';
import { useCartSummaryContext } from 'src/Layout/context/CartSummary';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { RESELLEE_WRITE } from 'src/modules/permissions';

import { SearchField } from '../../SearchField';
import { SelectContactForm } from '../../SelectContactForm';
import { useApplyCustomerProductOwner } from '../../hooks/useApplyCustomerProductOwner';
import type { Props } from '../../hooks/useModalType';
import { useReselleeSearch } from '../../hooks/useReselleeSearch';

export function CustomerSelect({
  onClose,
  openModal,
  currentRadioValue,
  setCurrentRadioValue,
  initialTotalCount = 0,
}: Props): React.MixedElement {
  const translator = useTranslator();
  const { __ } = translator;

  const locale = useAppSelector(localeSelector);
  const cartCustomer = useAppSelector(selectCartReselleeID);
  const customers = useAppSelector(selectCustomersList);

  const { productOwnerOrganization } = useCartSummaryContext();

  const sortedCustomers = React.useMemo(() => {
    return [...customers].sort((a, b) =>
      a.name.localeCompare(b.name, locale, { sensitivity: 'base' }),
    );
  }, [customers, locale]);

  const { filteredList, searchTerm, handleSearch, searching } =
    useReselleeSearch(sortedCustomers, initialTotalCount);

  const handleChangeCustomer = useApplyCustomerProductOwner({
    onSuccess: () => {
      setCurrentRadioValue();
      onClose();
    },
  });

  function handleEdit(value: string) {
    const customer = customers.find(cust => cust.id === value);

    if (!customer) return;

    setCurrentRadioValue(value);
    openModal({
      type: 'editAccountModal',
      orgId: value,
      actionOnSuccess: 'onBack',
      currentRole: 'CUSTOMER',
    });
  }

  function handleCreateCustomer() {
    openModal({
      type: 'createAccountModal',
      actionOnSuccess: 'onBack',
      currentRole: 'CUSTOMER',
    });
  }

  const radioValue =
    currentRadioValue || cartCustomer || productOwnerOrganization?.id;

  const customerFound = customers.some(customer => customer.id === radioValue);

  const currentQueryCustomersTotalCount =
    useAppSelector(selectCustomersCount) || 0;

  const listFooterNotice =
    currentQueryCustomersTotalCount > LIST_API_LIMIT
      ? __(
          'Only the first %(limit)s results are shown. Refine your search to see more.',
          { limit: LIST_API_LIMIT },
        )
      : undefined;

  return (
    <View gap={6} height="100%">
      {initialTotalCount > 4 && ( // based on the initial count to avoid the search field to disappear at the middle of the search
        <SearchField value={searchTerm} onChange={handleSearch} />
      )}
      {searching && <Loader />}

      {!searching && (
        <View
          as="div"
          gap={4}
          attributes={{ style: { marginBottom: 'var(--rs-unit-x1)' } }}
        >
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link icon={Plus} onClick={handleCreateCustomer}>
            {__('Create customer')}
          </Link>
        </View>
      )}

      {!searching && (
        <SelectContactForm
          type="customer"
          listFooterNotice={listFooterNotice}
          initialValue={customerFound ? radioValue : undefined}
          items={filteredList}
          notFoundMessage={
            searchTerm
              ? __('No customers found matching your search.')
              : undefined
          }
          handleEdit={handleEdit}
          handleSubmit={handleChangeCustomer}
          openModal={openModal}
          permissionsToSubmit={[RESELLEE_WRITE]}
        />
      )}
    </View>
  );
}
